import { submissionApi } from "../../constants/apiConstants";


/**
 * Calculates the date of birth based on the given age and incident date (@copilot).
 * @param {number} age - The age of the person.
 * @param {string} incidentDate - The incident date in string format (YYYY-MM-DD).
 * @returns {string} The calculated date of birth in string format (YYYY-MM-DD).
 */
function calculateBirthdateFromAge(age, dateOfIncident) {
  const incidentDate = new Date(dateOfIncident);
  let birthYear = incidentDate.getFullYear() - age;
  const monthDiff = incidentDate.getMonth() - incidentDate.getMonth();
  const dayDiff = incidentDate.getDate() - incidentDate.getDate();
  // Adjust the birth year if the incident happened before their birthday that year
  if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
    birthYear--;  // If the incident happened before their birthday, subtract one more year
  }
  // Create the birthdate using the adjusted birth year, with default month and day values.
  const birthDate = new Date(birthYear, 1, 1);

  return birthDate.toISOString().split('T')[0];  // Return the birthdate in 'YYYY-MM-DD' format
}



/**
 * Calculates the age based on the given date of birth (@copilot).
 * @param {string} dateOfBirth - The date of birth in the format "YYYY-MM-DD".
 * @param {string} dateOfIncident - The incident date in string format (YYYY-MM-DD).
 * @returns {number} The calculated age.
 */
function calculateAgeAtIncident(dateOfBirth, dateOfIncident) {
  const birthDate = new Date(dateOfBirth);
  const incidentDate = new Date(dateOfIncident);
  let age = incidentDate.getFullYear() - birthDate.getFullYear();
  // Check if the incident occurs before the birthday in the year of the incident.
  const monthDiff = incidentDate.getMonth() - birthDate.getMonth();
  const dayDiff = incidentDate.getDate() - birthDate.getDate();
  // If the incident is before the user's birthday in the incident year, reduce age by 1.
  if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
    age--;
  }
  return age;
}


/**
 * Calculates the date of birth based on the given age in days and incident date(@copilot).
 * @param {number} ageInDays - The age of the person in days.
 * @param {string} dateOfIncident - The incident date in string format (YYYY-MM-DD).
 * @returns {string} The calculated date of birth in string format (YYYY-MM-DD).
 */
function calculateBirthdateFromAgeInDays(ageInDays, dateOfIncident) {
  // Parse the incident date into a Date object
  const incidentDate = new Date(dateOfIncident);

  // Subtract the age in days from the incident date to get the birthdate
  const birthDate = new Date(incidentDate);
  birthDate.setDate(birthDate.getDate() - ageInDays); // Subtract age in days
  birthDate.setHours(0, 0, 0, 0);

  // Set the birth year to the calculated year and ensure January 5th as the month and day
  const birthYear = birthDate.getFullYear(); // Get the calculated birth year
  birthDate.setFullYear(birthYear); // Set the birth year
  birthDate.setMonth(0);  // Set month to January (0 in JS)
  birthDate.setDate(5);   // Set the day to 5th
  return birthDate.toISOString().split('T')[0];  // Return the birthdate in 'YYYY-MM-DD' format
}


/**
 * Calculates the age in days based on the given date of birth and incident date(@copilot).
 * @param {string} dateOfBirth - The date of birth in the format "YYYY-MM-DD".
 * @param {string} dateOfIncident - The incident date in string format (YYYY-MM-DD).
 * @returns {number} The calculated age in days.
 */
function calculateAgeInDaysAtIncident(dateOfBirth, dateOfIncident) {
  // Parse the date of birth and date of incident into Date objects
  const birthDate = new Date(dateOfBirth);
  const incidentDate = new Date(dateOfIncident);

  // Calculate the difference in time (in milliseconds)
  const timeDiff = incidentDate - birthDate;

  // Convert the difference in time from milliseconds to days
  const ageInDays = Math.floor(timeDiff / (1000 * 60 * 60 * 24));  // Convert ms to days

  return ageInDays;
}


export function createCaseDataObject(caseDataObject, caseId = null) {

  let caseDataReturnObj = {};
  Object.keys(caseDataObject).forEach(key =>{
    caseDataReturnObj[key] = caseDataObject[key];
  });
  const currentDate = new Date();
  const dateofBirth = new Date(caseDataReturnObj.dateOfBirth);
  const dateofIncident = new Date(caseDataReturnObj.injuryDate);
  const calculatedAge = calculateAgeInDaysAtIncident(dateofBirth, dateofIncident);
  const hiredDate = new Date(caseDataReturnObj.employeeHiredDate);
  const yearDiff = currentDate.getFullYear() - hiredDate.getFullYear();
  const monthDiff = (yearDiff * 12) + currentDate.getMonth() - hiredDate.getMonth();
  let calculatedLos = 0;
  if (monthDiff < 3) {
    calculatedLos = 0;
  }
  else if (monthDiff >= 3 && monthDiff <=11) {
    calculatedLos = 1;
  }
  else if (monthDiff >= 12 && monthDiff <=60) {
    calculatedLos = 2;
  }
  else {
    calculatedLos = 3;
  }
  let establishment_id_value = 0;
  if (caseDataReturnObj.establishment) {
    establishment_id_value = caseDataReturnObj.establishment.id;
  }
  return {
      case_number :  caseDataReturnObj.caseNumber,
      job_title :caseDataReturnObj.jobTitle,
      case_classification :caseDataReturnObj.mostSeriousOutcome,
      date_of_incident :caseDataReturnObj.injuryDate,
      time_of_incident: caseDataReturnObj.timeOfEvent,
      incident_location: caseDataReturnObj.injuryPlace,
      injury_illness_type: caseDataReturnObj.selectInjury,
      date_of_death: caseDataReturnObj.dateOfDeath,
      dafw_days: caseDataReturnObj.awayFromWork,
      djtr_days: caseDataReturnObj.jobTransferRestriction,
      dateOfBirth: caseDataReturnObj.dateOfBirth,
      hired_date: caseDataReturnObj.employeeHiredDate,
      sex: caseDataReturnObj.employeeGender,
      treatment_emergency_room: caseDataReturnObj.emergencyRoom,
      treatment_hospitalized: caseDataReturnObj.treatmentPatient,
      time_started_work: caseDataReturnObj.timeEmployeeBegan,
      time_unknown: caseDataReturnObj.timeUknown,
      nar_before_incident: caseDataReturnObj.beforeIncidentOccured,
      nar_what_happened: caseDataReturnObj.ilnessOrInjuryOccured,
      nar_injury_illness: caseDataReturnObj.theInjuryOrIllness,
      nar_object_substance: caseDataReturnObj.objectHarmEmployee,
      id: caseDataReturnObj.id || caseId,
      establishmentId: establishment_id_value || null,
      age: calculatedAge || 0,
      length_of_service: calculatedLos,
      short_description: caseDataReturnObj.incidentDescription,
      establishment: {id: establishment_id_value},
      submission_method: 1,
  };
}

export function createcaseDataDataObject(caseData) {

  //function to get first of the month 3 months ago, 11 months ago, or 5 years ago dependent on length of service.
const getFirstOfTheMonthForDOH = (los) => {
  const date = new Date();
  
  switch (los) {
    case 0: // 3 months ago
      date.setMonth(date.getMonth() - 3);
      break;
    case 1: // 11 months ago
      date.setMonth(date.getMonth() - 11);
      break;
    case 2: // 5 years ago
      date.setFullYear(date.getFullYear() - 5);
      break;
    case 3: // 5 years ago
      date.setFullYear(date.getFullYear() - 5);
      break;
    default:
      throw new Error("User's length of service is undefined.");
  }

  date.setDate(1); // Set to the first of the month

  // Format the date as 'YYYY-MM-DD'
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Ensure month is 2 digits
  const day = date.getDate().toString().padStart(2, '0'); // Ensure day is 2 digits

  return `${year}-${month}-${day}`;
}

  let formattedDateOfBirth = '';
  let formattedHiredDate = caseData['hired_date'];
  if (caseData['age']) {
    formattedDateOfBirth = calculateBirthdateFromAgeInDays(caseData['age'], caseData['date_of_incident']);
  }

  return {
    id: caseData.id || null,
    caseNumber: caseData.case_number,
    jobTitle: caseData.job_title,
    mostSeriousOutcome :caseData.case_classification,
    injuryDate: caseData.date_of_incident,
    timeOfEvent: caseData.time_of_incident,
    injuryPlace: caseData.incident_location,
    selectInjury: caseData.injury_illness_type,
    dateOfDeath: caseData.date_of_death,
    awayFromWork: caseData.dafw_days,
    jobTransferRestriction: caseData.djtr_days,
    dateOfBirth: formattedDateOfBirth,
    age: caseData.age,
    length_of_service: caseData.length_of_service,
    employeeHiredDate: formattedHiredDate,
    employeeGender: caseData.sex || caseData.gender,
    emergencyRoom: caseData.treatment_emergency_room,
    treatmentPatient: caseData.treatment_hospitalized,
    timeEmployeeBegan: caseData.time_started_work,
    timeUknown: caseData.time_unknown,
    beforeIncidentOccured: caseData.nar_before_incident,
    ilnessOrInjuryOccured: caseData.nar_what_happened,
    theInjuryOrIllness: caseData.nar_injury_illness,
    objectHarmEmployee: caseData.nar_object_substance,
    incidentDescription: caseData.short_description,
  };
}





/** WEBPACK FOOTER **
 ** ./src/components/caseData/caseDataModel.js
 **/